import React, { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Modal,
  Popconfirm,
  Row,
  Skeleton,
  Typography,
} from 'antd'
import { useDispatch } from 'react-redux'
import { useBillingSelector } from 'store/billingSlice/billingReducer'
import {
  getBillingTiers,
  updateBillingTier,
} from 'store/billingSlice/billingActions'
import { formatAmount } from 'utils'
import { useAuthSelector } from 'store/authSlice/authReducer'
import { Can } from 'permissions'
import { config } from 'configs'
import moment from 'moment'
const { Title, Text } = Typography

const ChangePlanModal = ({ visible, onClose, organizationTier }) => {
  const dispatch = useDispatch()
  const { organization } = useAuthSelector()
  const {
    loading: { fetchingTiers, updatingTier },
    tiers,
    subscription,
  } = useBillingSelector()
  const [selected, setSelected] = useState(organizationTier)

  useEffect(() => {
    dispatch(getBillingTiers(organization?.id))
  }, [dispatch, organization?.id])

  const handleChangePlan = () => {
    const organizationId = organization?.id
    const payload = {
      tierId: selected,
    }
    dispatch(updateBillingTier(organizationId, payload))
  }

  const daysPassedFromLastTierUpdate = moment().diff(
    moment(subscription?.TierLastUpdatedAt || new Date(0)),
    'days',
  )
  const isLastTierUpdatedWithin30Days = daysPassedFromLastTierUpdate < 30
  const daysLeftForNextTierUpdate = 30 - daysPassedFromLastTierUpdate

  const canNotUpdateTierMessage = () => (
    <>
      You have already updated your tier. You can update again after{' '}
      {daysLeftForNextTierUpdate}{' '}
      {daysLeftForNextTierUpdate === 1 ? `day` : `days`} or{' '}
      <span
        className="t-text-[#1BA1E2] t-font-semibold t-cursor-pointer"
        onClick={() => window.open(config.getHelpUrl, '_blank')}
      >
        contact support
      </span>{' '}
      to request changes
    </>
  )
  return (
    <Modal
      centered
      keyboard
      title={
        <Title level={3} style={{ margin: 0 }}>
          Change Plan
        </Title>
      }
      open={visible}
      onCancel={() => {
        onClose()
      }}
      footer={null}
      destroyOnClose
    >
      <Skeleton loading={fetchingTiers} active paragraph={{ rows: 16 }}>
        <Row gutter={[0, 16]}>
          {tiers?.map((tier, index) => (
            <Col
              key={index}
              span={24}
              className="t-cursor-pointer"
              onClick={() => setSelected(tier?.TierId)}
            >
              <TierCard
                title={tier?.TierName}
                description={tier?.Description}
                price={tier?.BaseFee}
                selected={selected === tier?.TierId}
              />
            </Col>
          ))}
          <Col span={24}>
            <Text>
              <Text strong>Note:</Text>{' '}
              {isLastTierUpdatedWithin30Days
                ? canNotUpdateTierMessage()
                : `Plan can be changed once every 30 days.
              On change, any payment due till now will be processed immediately.`}
            </Text>
          </Col>
        </Row>
        <Can
          I={config.actions.edit}
          a={config.components.organizationSubscription}
          passThrough
        >
          {(allowed) => (
            <Row justify="space-between" align="bottom" className="t-mt-8">
              <Col>
                <Button
                  style={{ backgroundColor: '#a8aaae29', borderRadius: '8px' }}
                  disabled={!allowed}
                  onClick={() => onClose()}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Popconfirm
                  title="Are you sure you want to change plan?"
                  onConfirm={() => handleChangePlan()}
                  disabled={!allowed || isLastTierUpdatedWithin30Days}
                >
                  <Button
                    type="primary"
                    style={{ borderRadius: '8px' }}
                    loading={updatingTier}
                    disabled={!allowed || isLastTierUpdatedWithin30Days}
                  >
                    Update Plan
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          )}
        </Can>
      </Skeleton>
    </Modal>
  )
}

const TierCard = ({ title, description, price, selected }) => (
  <Card
    bordered
    className={
      selected
        ? 't-border-[2px] t-rounded-[22px] t-border-[#1BA1E2]'
        : 't-border-[1px] t-rounded-[22px] t-border-[rgba(0, 0, 0, 0.15)]'
    }
  >
    <Row justify="space-between" align="middle">
      <Col>
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        <Text>
          {description}{' '}
          <Text
            underline
            style={{ color: '#1ba1e2' }}
            onClick={() => {
              window.open('https://extensia.cloud/pricing/', '_blank')
            }}
          >
            View details.
          </Text>
        </Text>
      </Col>
      <Col>
        <Text strong style={{ fontSize: '1.375rem' }}>
          {formatAmount(price)}
        </Text>
        <Text>/mo</Text>
      </Col>
    </Row>
  </Card>
)

export { ChangePlanModal }
